import React, { useEffect, useMemo, useRef, useState } from 'react';
import Zana, { ZanaState } from './Zana';
import {
  askZana, AskPageContext, AskTurn, fetchZanaBriefing, warmZanaContext,
  fetchZanaUsage, ZanaUsage,
  fetchZanaSync, fetchZanaGuide,
} from '../services/briefingsApi';
import { asList } from '../utils/asList';
import {
  ZanaIntent, ZanaIntentExecutor, isExecutableIntent, intentNeedsConfirm, isNavigateIntent, labelForZanaIntent,
} from '../services/zanaActions';
import { shapeAskResponse, isAffirmation, isDenial, isGuideInterrupt, restingFooter,
  HeldConfirm, armHeldConfirm, heldConfirmIsLive, threadMovedUnder, newZanaMsgId, staleHeldConfirmReply,
  BRANCH_SWITCHED_REPLY, chipMayRun, wantsGuidedWalk,
} from '../services/zanaAskShared';
import { localAskZana } from '../services/zanaLocalAsk';
import { auth } from '../firebase';
import {
  loadActiveConversation,
  saveActiveConversation,
  subscribeZanaChat,
  appendZanaNotice,
  archiveActiveConversation,
  ZanaChatMsg,
} from '../services/zanaChatStore';
import { episodesFromChat } from '../services/zanaEpisodes';
import type { ZanaEpisode } from '../services/zanaEpisodes';
import {
  ZANA_CHAT_STYLES,
  ZanaContextBar,
  ZanaMessageBubble,
  ZanaThinkingBubble,
  ZanaMsgSource,
} from './ZanaChatUi';

/* ============================================================================
   ZanaDock — Zana's always-on home. A floating copilot docked BOTTOM-LEFT of the
   fleet console, present on every tab (mounted once in the shell). Premium/
   Enterprise only — Basic never sees it.

   She:
   • shows a live status face (online / thinking / offline / reconnecting /
     "taking a break"),
   • rotates her latest briefing lines ("Zana says…"),
   • takes questions — "Ask Zana about your org" — answered from the org's
     own data via /api/premium/zana/ask (aggregate → model, deterministic
     fallback), so she works no matter which AI provider is configured,
   • Phase 1: renders navigate action chips from allow-listed intents; the
     parent (BusTracking) executes them in hard-coded UI code,
   • Phase 2: read chips (list alerts by priority) run instantly and print
     their result into the chat; WRITE chips (mark alerts reviewed) flip to an
     inline Confirm / Cancel step first — no mutation without a second tap.
   ========================================================================== */

interface Brief {
  source?: string;
  mascotState?: ZanaState;
  headline?: string;
  summary?: string;
  learningDaysLeft?: number;
  recommendations?: Array<{ text: string }>;
}
type Conn = 'online' | 'offline' | 'reconnecting';
type ChatMsg = ZanaChatMsg;

/** "about 4:20 PM" for a same-day reset, "tomorrow" once it crosses midnight —
 *  the human half of the resting message. Never mentions a model or a provider. */
function formatReset(ms?: number): string {
  if (!ms || !Number.isFinite(ms)) return 'shortly';
  const now = Date.now();
  if (ms <= now) return 'now';
  // More than ~14h out reads as "tomorrow" rather than a precise small-hours time.
  if (ms - now > 14 * 3600_000) return 'tomorrow';
  try {
    return new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return 'shortly';
  }
}

export default function ZanaDock({
  orgCode,
  premium,
  pageContext,
  liveCounts,
  onExecuteIntent,
  liveUnreadAlertCount = 0,
  liveHighRiskBusCount = 0,
  driving = false,
  takeoverActive = false,
  notice = null,
  noticeEpisode = null,
  onNoticeShown,
  /** Hide the floating pill (full-screen Ask Zana tab is open) without
   *  unmounting — so takeover notices still land in the shared thread. */
  hidden = false,
  onRequestCredits,
  /** Stop an in-flight Guide Mode / takeover — interrupt yields to chat. */
  onStopGuide,
}: {
  orgCode: string | null;
  premium: boolean;
  pageContext?: AskPageContext;
  /** What the open console can see right now (open incidents per bus, unread
   *  alerts…) — sent with every Guide request so a day-old finding the desk
   *  contradicts is dropped server-side (2026-09-29). */
  liveCounts?: Record<string, unknown>;
  onExecuteIntent?: ZanaIntentExecutor;
  /** Live unread fleetAlerts count from BusTracking — gates mark-reviewed chips. */
  liveUnreadAlertCount?: number;
  /** Live High Risk card count (incidents ∪ unread) — keeps her answers honest. */
  liveHighRiskBusCount?: number;
  /** True while the takeover panel is driving the console. The dock steps
   *  aside: two Zanas talking at once is worse than either. */
  driving?: boolean;
  /** True while a takeover is on screen in ANY phase — including the waiting /
   *  reading / paused stretches of a guided walk, where `driving` is false. */
  takeoverActive?: boolean;
  /** Something the console needs to say in the conversation — a walkthrough she
   *  had to refuse, for instance. Shown once, then cleared. */
  notice?: string | null;
  /** Structured agent episode for the current notice (Ask history v2). */
  noticeEpisode?: ZanaEpisode | null;
  onNoticeShown?: () => void;
  hidden?: boolean;
  /** Open the "get more Zana usage credits" surface. Optional: while the credits
   *  store is still being built, callers may leave it unset and the affordance
   *  stays hidden — the resting message and reset time still show regardless. */
  onRequestCredits?: () => void;
  /** Called when the owner interrupts Guide Mode from chat ("stop" / "enough"). */
  onStopGuide?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState<Brief | null>(null);
  const [conn, setConn] = useState<Conn>(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online');
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState('');
  const [chat, setChat] = useState<ChatMsg[]>([]);
  const [answerState, setAnswerState] = useState<ZanaState>('talking');
  const [rotIdx, setRotIdx] = useState(0);
  // Write-intent confirm state: which chip (msgIdx-intentIdx) is awaiting
  // Confirm/Cancel, and which one is currently executing.
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  /* The op Zana is currently holding, so a spoken "yes" can complete it.
     A ref, not state: it must be readable inside `send` without re-rendering
     the composer on every keystroke, and it is never rendered directly. */
  const pendingRef = useRef<HeldConfirm | null>(null);
  /** What was last asked, so a chip tapped on that answer knows whether the
   *  owner wanted to be walked through it or taken to it (2026-09-18). */
  const lastAskRef = useRef('');
  const [runningKey, setRunningKey] = useState<string | null>(null);
  // When a navigate chip is opening a page, show a brief "Opening …" bar + chip
  // glow so the jump reads as something Zana deliberately did, not a random tab
  // switch. Holds ~700ms because the actual navigation is an instant setState.
  const [openingLabel, setOpeningLabel] = useState<string | null>(null);
  /** Pending "Opening …" hold, so a newer tap can cancel it. */
  const openTimerRef = useRef<number | null>(null);
  /** Reasoning + email meters for the header bar. Null until first read (or if
   *  the org has no meter — the bar simply doesn't render). */
  const [usage, setUsage] = useState<ZanaUsage | null>(null);
  const [syncLabel, setSyncLabel] = useState<string | null>(null);
  const [coveragePct, setCoveragePct] = useState<number | null>(null);
  const [guideOffer, setGuideOffer] = useState<{
    headline: string;
    topic?: string;
    findingTitle?: string;
    findingKey?: string;
    busId?: string;
  } | null>(null);
  const [guideBadge, setGuideBadge] = useState(false);
  /** The org open NOW, for code resuming after an await — the closure's
   *  `orgCode` is the one the work started in. See BRANCH_SWITCHED_REPLY. */
  const orgRef = useRef(orgCode);
  orgRef.current = orgCode;
  const refreshUsage = React.useCallback(async (code: string) => {
    const u = await fetchZanaUsage(code);
    // 2026-09-17: a read that lands after a branch switch is the old branch's meter.
    if (u && code === orgRef.current) setUsage(u);
  }, []);
  const uid = auth.currentUser?.uid || null;
  const convIdRef = useRef<string | null>(null);
  const skipPersistRef = useRef(false);
  const guideCheckedRef = useRef<string | null>(null);
  useEffect(() => () => {
    if (openTimerRef.current !== null) window.clearTimeout(openTimerRef.current);
  }, []);

  /** The thread as the dock last rendered it — lets hydrate tell its own save
   *  apart from the page (or a notice) changing the thread underneath. */
  const chatRef = useRef<ChatMsg[]>(chat);
  chatRef.current = chat;

  // Shared thread with the full-screen Ask Zana tab.
  useEffect(() => {
    if (!orgCode || !uid || !premium) {
      setChat([]);
      convIdRef.current = null;
      pendingRef.current = null;
      setConfirmKey(null);
      return;
    }
    const hydrate = () => {
      const active = loadActiveConversation(orgCode, uid);
      /* 2026-09-17: the thread moved under the dock — the page answered, a
         notice landed, the thread was swapped or archived. Whatever the dock
         was holding was asked in a conversation that has since moved on: a
         "yes" typed here must not run it (the page's newer question is the
         one on screen). */
      if (threadMovedUnder(chatRef.current, active?.messages || [])) {
        pendingRef.current = null;
        setConfirmKey(null);
      }
      skipPersistRef.current = true;
      setChat(active?.messages || []);
      convIdRef.current = active?.id || null;
      queueMicrotask(() => { skipPersistRef.current = false; });
    };
    hydrate();
    return subscribeZanaChat(hydrate);
  }, [orgCode, uid, premium]);

  useEffect(() => {
    if (!orgCode || !uid || !premium || skipPersistRef.current) return;
    const saved = saveActiveConversation(orgCode, uid, chat, convIdRef.current);
    if (saved) convIdRef.current = saved.id;
  }, [chat, orgCode, uid, premium]);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const askInputRef = useRef<HTMLTextAreaElement | null>(null);
  // Tap anywhere outside the dock to collapse it back to the "Ask Zana" pill.
  // Stay open for the whole walk — Ask is the escape hatch; collapsing it
  // behind a pill while she drives re-buries "stop".
  useEffect(() => {
    if (!open || takeoverActive) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, takeoverActive]);

  /* 2026-09-17: a branch switch starts from a clean Guide slate. The fetches
     below only ever set truthy values, so without this branch B showed branch
     A's headline and learning line, and "Show me" walked B with A's topic.
     Declared before the fetches so the reset lands first in the same commit. */
  useEffect(() => {
    setGuideOffer(null);
    setGuideBadge(false);
    setSyncLabel(null);
    setCoveragePct(null);
    // …and nothing held from the last branch may be confirmed in this one: the
    // executor acts on whichever org is open when the "yes" arrives.
    pendingRef.current = null;
    setConfirmKey(null);
  }, [orgCode]);

  /* 2026-09-17: when the full Ask Zana page opens, the offer is the page's to
     show. Skipping the check while hidden was not enough: an offer the dock
     already held came back on the dashboard after "Later" on the page. */
  useEffect(() => {
    if (!hidden) return;
    setGuideOffer(null);
    setGuideBadge(false);
  }, [hidden]);

  // Latest briefing (headline drives the resting message + face). Read via the
  // backend — client Firestore reads of aiBriefings are rules-gated and deny
  // some org admins, which would leave Zana permanently "taking a break".
  useEffect(() => {
    if (!orgCode || !premium) return;
    let alive = true;
    fetchZanaBriefing(orgCode)
      .then((b) => { if (alive) setBrief(b as Brief | null); })
      .catch(() => { if (alive) setBrief(null); });
    fetchZanaSync(orgCode).then((s) => {
      if (!alive || !s) return;
      if (s.learningLabel) setSyncLabel(s.learningLabel);
      if (typeof s.coveragePct === 'number') setCoveragePct(s.coveragePct);
    }).catch(() => {});
    return () => { alive = false; };
  }, [orgCode, premium]);

  // Guide Mode — soft-open when Sync+Decide say she has something real.
  // Nobody asks "take over"; she notices on desk open / page change.
  /* 2026-09-17: the screen context rides a ref. This check is meant to fire on
     a PAGE change, and zanaAskContext is rebuilt whenever a live count moves,
     so depending on the object would re-ask /guide on every tick. */
  const guidePageContextRef = useRef(pageContext);
  guidePageContextRef.current = pageContext;
  const guideLiveRef = useRef(liveCounts);
  guideLiveRef.current = liveCounts;
  const guidePage = pageContext?.page || 'desk';
  /* WHAT THIS DESK HAS ALREADY OFFERED, AND WHEN (2026-09-28). The server
     now keeps a shown/dismissed finding quiet for thirty minutes — but only
     when the caller may write guide state, and only after the round trip. This
     is the desk's own memory of the same rule: a finding key it has offered in
     the last thirty minutes is not popped again on the next page change,
     whatever the server says, and closing the dock on an offer counts as
     waving it away. Gabriel: "it came thrice … it should be like every
     5 mins". Thirty minutes matches the server's window. */
  const offeredRef = useRef<Map<string, number>>(new Map());
  const OFFER_QUIET_MS = 30 * 60 * 1000;
  const recentlyOffered = (key: string | undefined) => {
    if (!key) return false;
    const at = offeredRef.current.get(key);
    return typeof at === 'number' && Date.now() - at < OFFER_QUIET_MS;
  };
  const noteOffered = (key: string | undefined) => { if (key) offeredRef.current.set(key, Date.now()); };
  useEffect(() => {
    /* `hidden` means the full Ask Zana page is open, and it runs this same
       check itself. Two checks meant two /guide calls per open (double usage
       stats skewing Sync Learner) and a hidden dock holding its own copy of
       the offer — which came back after "Later" on the page. */
    /* `takeoverActive`, not `driving`: a guided walk flips driving <-> waiting
       on every Next, and each flip re-ran this check (and, with the release
       below, re-POSTed /guide) — one landing mid-walk popped the dock open. */
    if (!orgCode || !premium || takeoverActive || hidden) return;
    const key = `${orgCode}:${guidePage}`;
    if (guideCheckedRef.current === key) return;
    guideCheckedRef.current = key;
    let alive = true;
    let landed = false;
    fetchZanaGuide(orgCode, { mode: 'proactive', pageContext: guidePageContextRef.current, liveCounts: guideLiveRef.current })
      .then((g) => {
        if (!alive) return;
        landed = true;
        if (!g || !g.decision) return;
        if (g.learningLabel) setSyncLabel(g.learningLabel);
        if (typeof g.coveragePct === 'number') setCoveragePct(g.coveragePct);
        if (g.decision.softOpen && g.decision.headline && recentlyOffered(g.decision.finding?.key)) {
          // Offered here within the window — badge only, no pop-up.
          setGuideBadge(true);
          setGuideOffer(null);
        } else if (g.decision.softOpen && g.decision.headline) {
          noteOffered(g.decision.finding?.key);
          setGuideOffer({
            headline: g.decision.headline,
            topic: g.decision.lens?.topic,
            findingTitle: g.decision.finding?.title,
            findingKey: g.decision.finding?.key,
            busId: g.decision.finding?.busId,
          });
          setGuideBadge(false);
          // Peek the dock so "has something to tell you" is visible without a click.
          setOpen(true);
        } else if (g.decision.badge) {
          setGuideBadge(true);
          setGuideOffer(null);
        } else {
          setGuideOffer(null);
          setGuideBadge(false);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
      /* A check cancelled before it answered never happened. The key used to
         stay claimed, so a takeover starting mid-fetch cancelled it, the re-run
         after it skipped on the key, and that page's offer was lost. */
      if (!landed && guideCheckedRef.current === key) guideCheckedRef.current = null;
    };
  }, [orgCode, premium, takeoverActive, hidden, guidePage]);

  // Read the usage meters when the dock opens (and when the org changes while
  // open). One cheap read; the bar is a nicety and silently absent on failure.
  useEffect(() => {
    if (!open || !orgCode || !premium) return;
    void refreshUsage(orgCode);
  }, [open, orgCode, premium, refreshUsage]);

  // Connection awareness (the offline / reconnecting states).
  useEffect(() => {
    const goOffline = () => setConn('offline');
    const goOnline = () => {
      setConn('reconnecting');
      window.setTimeout(() => setConn(navigator.onLine ? 'online' : 'offline'), 2200);
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  const messages = useMemo(() => {
    if (!brief || brief.source === 'learning') return [];
    const out: string[] = [];
    if (brief.headline) out.push(brief.headline);
    if (brief.summary) out.push(brief.summary);
    asList<{ text?: string }>(brief.recommendations).slice(0, 2).forEach((r) => out.push(`Tip — ${r.text}`));
    return out.length ? out : ["I'm watching your fleet — all quiet right now."];
  }, [brief]);

  // Rotate resting messages when idle + online.
  useEffect(() => {
    if (conn !== 'online' || asking || chat.length || messages.length < 2) return;
    const iv = window.setInterval(() => setRotIdx((i) => (i + 1) % messages.length), 6000);
    return () => window.clearInterval(iv);
  }, [conn, asking, chat.length, messages.length]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [chat, asking]);

  /* Say hello, once, on the first dashboard of the session.
   *
   * The dock had no auto-open logic at all: `open` started false and was only
   * ever set true by the user's own click or by a takeover notice being pushed
   * in. So an owner who never noticed the collapsed pill never met Zana, and
   * the greeting she is perfectly capable of giving — with this org's live
   * High Risk numbers in it — was something you had to go looking for.
   *
   * Deliberately restrained: once per session per org, only on a clean slate,
   * never while she is driving or while the full-screen tab is open, and after
   * a beat so it arrives on a painted dashboard rather than a loading one.
   * `localAskZana('hello', …)` builds the line so the copy and the numbers
   * live in exactly one place.
   */
  const greetedRef = useRef(false);
  useEffect(() => {
    if (greetedRef.current) return;
    if (!premium || !orgCode || !uid || hidden || driving) return;
    if (chat.length > 0) return;
    const key = `zana_greeted_v1:${orgCode}:${uid}`;
    try {
      if (sessionStorage.getItem(key)) { greetedRef.current = true; return; }
    } catch { /* private mode — greet anyway, just not remembered */ }
    /* The latch is set when she ACTUALLY greets, not when we schedule it.
       Setting it here meant the first attempt claimed the only chance: this
       effect depends on brief, liveUnreadAlertCount and liveHighRiskBusCount,
       which change on live data, so any tick inside the 1.2s beat ran the
       cleanup, cleared the timer, re-entered — and returned immediately on
       `greetedRef.current`. On a busy fleet the greeting was cancelled before
       it fired and never re-armed, so nobody ever saw it (review, 09-06). */
    const t = window.setTimeout(() => {
      const hello = localAskZana('hello', {
        brief,
        unreadAlertCount: liveUnreadAlertCount,
        highRiskBusCount: liveHighRiskBusCount,
      });
      if (!hello?.answer) return;
      setOpen(true);
      greetedRef.current = true;
      setChat((c) => (c.length ? c : [{ from: 'zana', text: hello.answer as string }]));
      try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ }
    }, 1200);
    return () => window.clearTimeout(t);
  }, [premium, orgCode, uid, hidden, driving, chat.length, brief,
    liveUnreadAlertCount, liveHighRiskBusCount]);

  /* Archive on unload — the effect the full-screen page had and the dock did
     not. A thread held only in the dock was destroyed by any hard navigation
     with no History entry to recover it, which is the other half of "dock
     truncates history after navigation". Idempotent, so firing twice is fine. */
  useEffect(() => {
    if (!orgCode || !uid) return;
    const archive = () => archiveActiveConversation(orgCode, uid);
    const onHide = () => { if (document.visibilityState === 'hidden') archive(); };
    window.addEventListener('pagehide', archive);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', archive);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [orgCode, uid]);

  /* Walk starts → Copilot opens. Ask is the escape hatch ("stop", questions);
     the bottom panel steers tabs. Hiding Ask during a walk buried the interrupt
     behind a collapsed pill while the subject screen was supposed to be the
     focus — open the thread so typing "stop" is one word away. */
  useEffect(() => {
    if (takeoverActive && !hidden) setOpen(true);
  }, [takeoverActive, hidden]);

  // Takeover summaries / refusals land in the shared thread (dock + full page).
  const noticeSeen = useRef<() => void>();
  noticeSeen.current = onNoticeShown;
  useEffect(() => {
    if (!notice || !orgCode || !uid) return;
    appendZanaNotice(orgCode, uid, notice, noticeEpisode);
    // Not a receipt: refusals and "back in chat" land here too. Real receipts
    // are recorded where they are made — useZanaTakeover's onSummary.
    // A notice moves the conversation on; nothing held survives it.
    pendingRef.current = null;
    setConfirmKey(null);
    if (!hidden) setOpen(true);
    noticeSeen.current?.();
  }, [notice, noticeEpisode, orgCode, uid, hidden]);

  /* HOOKS RUN BEFORE THE EARLY RETURN, ALWAYS.
   *
   * This `useMemo` sat BELOW `if (!premium || !orgCode || hidden) return null;`,
   * so on any render where the dock is hidden it was never called — and React
   * requires the same hooks, in the same order, on every render.
   *
   * It did not merely warn: CRA promotes react-hooks/rules-of-hooks to an ERROR
   * in a production build, so the Render deploy failed outright while local
   * `tsc --noEmit` passed, because type-checking cannot see a lint rule. That is
   * why a green typecheck still shipped nothing.
   *
   * `learning` moves up with it because the memo reads it. */
  const learning = brief?.source === 'learning';
  /* Starter prompts tuned to the screen the owner is on. She already receives
     pageContext.page, so predicting what they'd ask about THIS tab is smoother
     than a blank box. Kept to three; a wall of chips is its own noise. */
  const suggestedPrompts = useMemo(() => {
    if (conn !== 'online' || learning) return [];
    const page = String(pageContext?.page || '');
    if (page.startsWith('analytics:fuel') || page === 'fuelReport' || page === 'fuel') {
      return ['Which bus is driving my fuel spend?', 'Is any bus lying to me about fuel?', 'What should I fix first?'];
    }
    if (page.startsWith('analytics:routes')) {
      return ['How close is each route to being learned?', 'Which route has slipped?', 'What can you tell me yet?'];
    }
    if (page.startsWith('analytics:drivers')) {
      return ['Who needs coaching?', 'How many drivers have you rated?', 'Who is my most consistent driver?'];
    }
    if (page.startsWith('analytics:incidents')) {
      return ['What should I investigate first?', 'Any bus due for service?', 'Is there a pattern in incidents?'];
    }
    if (page === 'highRiskAlerts') {
      return ['What needs me right now?', 'Which of these can wait?', 'Clear the routine ones'];
    }
    return ['Where is my money leaking?', 'What should I focus on this week?', 'Tell me something I should know'];
  }, [conn, learning, pageContext?.page]);

  if (!premium || !orgCode || hidden) return null;

  const face: ZanaState =
    conn === 'offline' ? 'offline'
      : conn === 'reconnecting' ? 'reconnecting'
        : asking ? 'thinking'
          : chat.length ? answerState
            : learning ? 'thinking'
              : brief ? (brief.mascotState || 'online') : 'sleeping';



  const restLine =
    conn === 'offline'
      ? "I'm offline from the live server — I can still greet you, read High Risk on this screen, or open a tab."
      : conn === 'reconnecting' ? 'Reconnecting to your fleet…'
        /* Not the offer's headline: that is already on the card above, and
           reading it twice with the starter prompts underneath made the two
           look unrelated (Gabriel, 2026-09-28: "How do those relate??"). While
           an offer is up, this line points at it. */
        : guideOffer ? "That's the one thing I'd look at first — Show me opens it."
          : (syncLabel || learning)
            ? `${syncLabel || `I'm still learning your fleet${brief?.learningDaysLeft ? ` — insights in ~${brief.learningDaysLeft} day(s)` : ' — insights begin soon'}.`}${coveragePct != null ? ` — ${coveragePct}% of your fleet mapped so far` : ''}`
            : brief ? messages[rotIdx % Math.max(1, messages.length)]
              : "Taking a short break — I'll have your briefing ready shortly.";

  async function send(override?: string) {
    // A suggested-prompt chip passes its text directly — setQuestion is async,
    // so reading `question` right after setting it would send the old value.
    const q = (override ?? question).trim();
    if (!q) return;
    lastAskRef.current = q;
    if (asking) {
      // Enter DID fire — `send` was dropping the keystroke silently while a
      // previous ask was in flight (up to ~70s with the timeout and retry),
      // which is indistinguishable from a dead Enter key. Say so instead.
      setChat((c) => [...c, {
        from: 'zana',
        text: "Still working on the last one — give me a second and ask again.",
      }]);
      return;
    }

    /* Guide Mode interrupt — "stop" / "enough" / "later" always yields to chat.
       Nobody should need Escape or a panel button to get Ask back. */
    if (isGuideInterrupt(q) && (takeoverActive || guideOffer)) {
      setChat((c) => [...c, { from: 'you', text: q }]);
      setQuestion('');
      if (askInputRef.current) askInputRef.current.style.height = 'auto';
      /* 2026-09-17: "cancel" / "stop" / "never mind" are denial words too, and
         this branch returns before the denial check below — so it has to
         disarm whatever op she is holding, or a later "ok" runs it. */
      pendingRef.current = null;
      setConfirmKey(null);
      if (guideOffer) {
        setGuideOffer(null);
        if (orgCode) void fetchZanaGuide(orgCode, { text: q, mode: 'reactive', pageContext, liveCounts });
      }
      if (takeoverActive) onStopGuide?.();
      setOpen(true);
      setChat((c) => [...c, {
        from: 'zana',
        text: "Okay — I'm back in chat. Ask me anything.",
      }]);
      return;
    }

    /* Saying yes out loud.

       The armed state lived only in a React key tied to a chip's position, so
       a spoken "yes, confirm" reached the engine as a brand new question,
       re-armed the same op, and invited the same "yes" — for ever. Zana
       suggests these words herself ("say 'park them'", "Confirm below"), so
       they have to complete the op she is holding. */
    const held = pendingRef.current;
    if (held && (isAffirmation(q) || isDenial(q))) {
      setQuestion('');
      if (askInputRef.current) askInputRef.current.style.height = 'auto';
      pendingRef.current = null;
      setConfirmKey(null);
      /* 2026-09-17: only while it is still the question on the table — same
         org, no takeover on screen, within the TTL, and her asking message
         still the last thing in this thread. See HeldConfirm. */
      if (!heldConfirmIsLive(held, { orgCode, chat, takeoverActive, now: Date.now() })) {
        setChat((c) => [...c, { from: 'you', text: q }, { from: 'zana', text: staleHeldConfirmReply(isAffirmation(q)) }]);
        return;
      }
      if (isAffirmation(q)) {
        setChat((c) => [...c, { from: 'you', text: q }]);
        await runIntent(held.intent, `spoken-${held.intent.actionId}`);
        return;
      }
      setChat((c) => [...c,
        { from: 'you', text: q },
        { from: 'zana', text: `Left it alone — nothing was ${labelForZanaIntent(held.intent).toLowerCase()}.` },
      ]);
      return;
    }
    // A new question supersedes whatever was on the table.
    pendingRef.current = null;
    // Snapshot BEFORE appending the new question — this is the history the
    // answer should condition on (the backend caps + sanitizes it again).
    const history: AskTurn[] = chat.slice(-6);
    const episodes = episodesFromChat(chat, 3);
    setChat((c) => [...c, { from: 'you', text: q }]);
    setQuestion('');
    if (askInputRef.current) askInputRef.current.style.height = 'auto';
    setAsking(true);
    const askedIn = orgCode;
    const res = await askZana(
      orgCode!,
      q,
      pageContext,
      history,
      {
        unreadAlertCount: liveUnreadAlertCount,
        highRiskBusCount: liveHighRiskBusCount,
      },
      { brief },
      episodes,
    );
    setAsking(false);
    /* 2026-09-17: the owner switched branches while this was loading. The
       answer, its chips, its held write and its takeover / auto-read all belong
       to the branch it was asked in — none of it may land in this one. */
    if (orgRef.current !== askedIn) {
      if (orgRef.current) setChat((c) => [...c, { from: 'zana', text: BRANCH_SWITCHED_REPLY }]);
      return;
    }
    const shaped = shapeAskResponse(q, res, {
      unreadAlertCount: liveUnreadAlertCount,
      highRiskBusCount: liveHighRiskBusCount,
    });
    setAnswerState((shaped.mascotState as ZanaState) || 'talking');
    const answerId = newZanaMsgId();
    setChat((c) => [...c, {
      from: 'zana',
      id: answerId,
      ...(askedIn ? { orgCode: askedIn } : {}),
      text: shaped.text,
      intents: shaped.intents,
      offer: shaped.offer,
      source: (res.source as ZanaMsgSource) || undefined,
      // Resting → footer with the reset time + credits prompt. Only 'limited'
      // (resting) carries a limit; a genuine fault ('unavailable') never does.
      restingLimit: shaped.aiStatus === 'limited' && shaped.limit ? shaped.limit : undefined,
    }]);
    // A real model answer moves the meter — refresh the bar so it reflects the
    // spend this ask just made. Cheap, and only when the dock is open.
    if (orgCode) void refreshUsage(orgCode);
    // Hold the op she just offered, so "yes" above can complete it.
    pendingRef.current = armHeldConfirm(shaped.intents, shaped.text, { orgCode, msgId: answerId, now: Date.now() });

    /* The moment she offers to drive, start warming the context she will need.
     *
     * Composing a walkthrough is quick; rebuilding the org context in front of
     * it is not, and that rebuild is what Gabriel kept feeling as lag (bug 59).
     * Reading three chips and picking one is several seconds of human time that
     * was being spent on nothing — so spend it here instead. Not awaited: a
     * failed hint must never stop a walkthrough that would have worked. */
    if (shaped.offer && orgCode) void warmZanaContext(orgCode);

    // Explicit handover ("clear my alerts" / "take over and…") — the ask WAS
    // the consent. Casual answers only get the offer button, never auto-drive.
    if (shaped.autoScriptId && onExecuteIntent) {
      void startTakeover({
        scriptId: shaped.autoScriptId,
        ...(shaped.autoOps ? { ops: shaped.autoOps } : {}),
        ...(shaped.autoSkipChooser ? { skipChooser: true } : {}),
        ...(shaped.autoUnattended ? { auto: true, kind: 'do' } : { kind: 'show' }),
      });
    } else if (shaped.autoTopic && onExecuteIntent) {
      void startTakeover({
        topic: shaped.autoTopic,
        ...(shaped.autoSkipChooser ? { skipChooser: true } : {}),
        ...(shaped.autoUnattended ? { auto: true, kind: 'do' } : { kind: 'show' }),
      });
    } else if (shaped.autoRead && onExecuteIntent) {
      void runIntent(shaped.autoRead, `auto-${shaped.autoRead.actionId}`);
    }
  }

  /* 2026-09-17: every start_takeover from the dock goes through here. A
     takeover owns its writes (the panel asks its own Yes), so whatever the chat
     was holding is retired the moment one is dispatched — or "suspend bus X"
     armed the write here AND started the walk, and an "ok" typed after No on
     the panel suspended the bus anyway. */
  function startTakeover(params: NonNullable<ZanaIntent['params']> & { kind?: 'show' | 'do' }) {
    pendingRef.current = null;
    setConfirmKey(null);
    /* 2026-09-18 (Gabriel: "if you ask directly, it should just do it"): a
       composed subject goes STRAIGHT to the thing unless the owner asked to be
       walked through it. Named scripts (ops_*) keep their walk — they carry the
       write confirms. */
    const mode = params.mode
      || (params.scriptId ? 'walk' : (wantsGuidedWalk(lastAskRef.current) ? 'walk' : 'jump'));
    /* kind=show → teach walkthrough; kind=do / unattended act → FG agent. */
    const kind = params.kind
      || (params.auto === true ? 'do' : undefined)
      || (mode === 'walk' && wantsGuidedWalk(lastAskRef.current) ? 'show' : undefined);
    return onExecuteIntent?.({
      actionId: 'start_takeover',
      params: { ...params, mode, ...(kind ? { kind } : {}) },
    });
  }

  /* 2026-09-17: a line owed to the thread of the org it happened in. A write
     still running when the owner switched branches posted its receipt into the
     NEW branch's thread — "Done — marked 3 alert(s) reviewed" under a branch
     whose alerts were never touched. It goes to the branch it ran in. */
  function sayIn(org: string | null, text: string) {
    if (org && org !== orgRef.current) {
      if (uid) appendZanaNotice(org, uid, text);
      return;
    }
    /* THE SAME LINE TWICE IN A ROW IS A FAULT, NOT A MESSAGE (Gabriel,
       2026-09-28: a second "Show me" on the same finding "just wrote the same
       message down there … it looks like it doesn't know what it's doing").
       A landing line identical to the last thing she said is not said again;
       the jump itself still runs. */
    setChat((c) => {
      const last = c.length ? c[c.length - 1] : null;
      if (last && last.from === 'zana' && last.text === text) return c;
      return [...c, { from: 'zana', text }];
    });
  }

  /** Execute an intent via the parent. If the executor returns a line (read
   *  results, write receipts), append it to the chat as a Zana message. */
  async function runIntent(intent: ZanaIntent, key: string) {
    if (!onExecuteIntent || !isExecutableIntent(intent)) return;
    // A previous navigate is still holding its indicator: cancel it, or its
    // timer fires mid-flight and clears THIS chip's running state — re-enabling
    // a button that is still executing.
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    setConfirmKey(null);
    /* Disarm the spoken confirm too.
       `pendingRef` is cleared when a "yes" runs the op, but a CHIP tap runs
       the same op by a different route and used to leave it armed — so a
       "yes" typed straight afterwards would execute the write a SECOND time.
       Anything that executes the op must retire it. */
    pendingRef.current = null;
    setRunningKey(key);
    const navigating = isNavigateIntent(intent);
    if (navigating) {
      // "Open Fuel Report" → "Fuel Report"; leaves "Spotlight KCE998Z" intact.
      setOpeningLabel(labelForZanaIntent(intent).replace(/^Open\s+/i, ''));
    }
    const ranIn = orgCode;
    try {
      const result = await onExecuteIntent(intent);
      if (typeof result === 'string' && result) sayIn(ranIn, result);
    } catch {
      sayIn(ranIn, "That didn't go through — try again in a moment.");
      setRunningKey((cur) => (cur === key ? null : cur));
      setOpeningLabel(null);
      return;
    }
    if (navigating) {
      // Hold the indicator briefly — the navigation itself already happened.
      openTimerRef.current = window.setTimeout(() => {
        openTimerRef.current = null;
        // Only clear if this chip is still the running one; a newer tap owns it
        // otherwise.
        setRunningKey((cur) => (cur === key ? null : cur));
        setOpeningLabel(null);
      }, 700);
    } else {
      setRunningKey((cur) => (cur === key ? null : cur));
    }
  }

  function onChipTap(intent: ZanaIntent, key: string) {
    if (intentNeedsConfirm(intent)) {
      // First tap arms the confirm step; Confirm/Cancel below does the rest.
      setConfirmKey((k) => (k === key ? null : key));
      return;
    }
    void runIntent(intent, key);
  }

  const connDot = conn === 'online' ? '#54bd46' : conn === 'reconnecting' ? '#f5a623' : '#ff6b6b';

  return (
    <div
      className="zana-dock"
      ref={rootRef}
      aria-hidden={false}
      style={{
        // Above the takeover click-guard (5900) for the whole walk — not only
        // while `driving` (guided parks on waiting most of the time). Under the
        // panel (6600) so Stop/Carry on still win. Typing "stop" yields to chat.
        position: 'fixed', right: 18, bottom: 18, zIndex: takeoverActive ? 6550 : 5950,
        opacity: 1,
        pointerEvents: 'auto',
        transition: 'opacity .25s ease',
      }}
    >
      <style>{`
        .zana-dock *{box-sizing:border-box}
        .zana-dock .zd-input::placeholder{color:#a99fce}
        .zana-dock .zd-send:hover{background:#5a34c9}
        .zana-dock .zd-pill:hover{transform:translateY(-2px)}
        .zana-dock .zd-chip:hover{background:rgba(155,124,255,.35)}
        @keyframes zdIn{from{opacity:0;transform:translateY(10px) scale(.98)}to{opacity:1;transform:none}}
        @keyframes zdBar{0%{left:-45%}100%{left:100%}}
        @keyframes zdGlow{0%,100%{box-shadow:0 0 0 0 rgba(155,124,255,0)}50%{box-shadow:0 0 14px 2px rgba(155,124,255,.6)}}
        ${ZANA_CHAT_STYLES}
      `}</style>

      {!open && (
        <button
          className="zd-pill"
          onClick={() => setOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', border: 'none',
            background: guideOffer
              ? 'linear-gradient(120deg,#3a2060,#5a34c9)'
              : 'linear-gradient(120deg,#241a3a,#3a2b6b)',
            color: '#fff', borderRadius: 999,
            padding: '8px 16px 8px 8px', boxShadow: '0 16px 40px -14px rgba(30,18,60,.7)', transition: 'transform .18s',
            animation: guideOffer ? 'zdGlow 2.4s ease-in-out infinite' : undefined,
          }}
        >
          <Zana state={face} size={40} animated />
          <span style={{ fontSize: 13, fontWeight: 800, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {guideOffer ? 'Zana has something to tell you' : 'Ask Zana'}
          </span>
          {(guideOffer || guideBadge) && (
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f0a34a' }} />
          )}
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: connDot }} />
        </button>
      )}

      {open && (
        <div style={{
          width: 340, maxWidth: '92vw', borderRadius: 20, overflow: 'hidden', color: '#fff', animation: 'zdIn .22s ease both',
          background: 'radial-gradient(130% 130% at 82% -12%,#2f2058 0%,#1a1236 48%,#120d22 100%)',
          boxShadow: '0 30px 70px -24px rgba(30,18,60,.8)', border: '1px solid rgba(155,124,255,.25)',
        }}>
          {/* "Opening a page" indicator — a thin sweeping bar + caption so a
              Zana-driven navigation reads as deliberate. */}
          {openingLabel && (
            <div style={{ animation: 'zdMsg .2s ease both' }}>
              <div style={{ position: 'relative', height: 3, background: 'rgba(155,124,255,.16)', overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute', top: 0, height: '100%', width: '45%', borderRadius: 999,
                  background: 'linear-gradient(90deg,transparent,#9b7cff,transparent)',
                  animation: 'zdBar .7s ease-in-out infinite',
                }} />
              </div>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', color: '#c4b3ff', padding: '6px 16px 0' }}>
                Opening {openingLabel}…
              </div>
            </div>
          )}
          {/* header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px 10px' }}>
            <Zana state={face} size={56} animated ground />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontSize: 15, fontWeight: 800 }}>Zana</span>
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.08em', color: '#c4b3ff', background: 'rgba(155,124,255,.16)', padding: '2px 7px', borderRadius: 5 }}>LIVE COPILOT</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#a99fce', marginTop: 2 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: connDot }} />
                {conn === 'online' ? 'Watching your fleet' : conn === 'reconnecting' ? 'Reconnecting…' : 'Offline'}
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Minimize" style={{ background: 'transparent', border: 'none', color: '#a99fce', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}>–</button>
          </div>

          {/* reasoning meter — only when there's a real limit to show. A thin bar
              that stays quiet under 70% and turns amber while resting. Speaks of
              "Zana's reasoning", never a model. */}
          {usage && usage.ai.limit > 0 && (() => {
            const f = Math.max(0, Math.min(1, usage.ai.fraction));
            const resting = usage.ai.state === 'resting';
            const warm = resting || f >= 0.7;
            const barColor = resting ? '#f0a34a' : f >= 0.7 ? '#e6c84a' : '#9b7cff';
            return (
              <div style={{ padding: '0 16px 8px' }} title={`Zana's deep reasoning: ${usage.ai.used} of ${usage.ai.limit} in the last ${usage.ai.windowHours}h`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.08em', color: warm ? '#f3c58a' : '#8f86b6' }}>
                    {resting ? `RESTING · FULL DEPTH ${formatReset(usage.ai.resetAtMs) === 'tomorrow' ? 'TOMORROW' : `~${formatReset(usage.ai.resetAtMs)}`}` : 'ZANA REASONING'}
                  </span>
                  {warm && onRequestCredits && (
                    <button
                      type="button"
                      onClick={() => onRequestCredits?.()}
                      style={{ background: 'transparent', border: 'none', color: '#c4b3ff', fontSize: 9.5, fontWeight: 800, letterSpacing: '.04em', cursor: 'pointer', padding: 0 }}
                    >
                      More credits →
                    </button>
                  )}
                </div>
                <div style={{ height: 4, borderRadius: 999, background: 'rgba(255,255,255,.10)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round(f * 100)}%`, background: barColor, borderRadius: 999, transition: 'width .4s ease' }} />
                </div>
              </div>
            );
          })()}

          {/* conversation / resting message */}
          <ZanaContextBar turnCount={chat.length} compact />
          <div ref={bodyRef} className="zana-scroll-hide" style={{ maxHeight: 260, overflowY: 'auto', padding: '2px 16px 8px' }}>
            {guideOffer && (
              <div style={{
                marginBottom: 10, padding: '10px 12px', borderRadius: 12,
                background: 'rgba(155,124,255,.14)', border: '1px solid rgba(155,124,255,.35)',
              }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', color: '#c4b3ff', marginBottom: 4 }}>
                  GUIDE MODE
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.45, color: '#eee9fb', marginBottom: 10 }}>
                  {guideOffer.headline}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="zd-chip"
                    onClick={() => {
                      const offer = guideOffer;
                      setGuideOffer(null);
                      setOpen(true);
                      void (async () => {
                        const askedIn = orgCode;
                        const g = orgCode
                          ? await fetchZanaGuide(orgCode, { text: 'show me', mode: 'reactive', pageContext })
                          : null;
                        // 2026-09-17: the offer was this branch's; a switch
                        // while it loaded must not start A's walk in B.
                        if (orgRef.current !== askedIn) return;
                        const topic = g?.decision?.lens?.topic || offer?.topic || 'fleet';
                        const findingTitle = g?.decision?.finding?.title || offer?.findingTitle;
                        const busId = g?.decision?.finding?.busId || offer?.busId;
                        /* 2026-09-18: this used to promise a walkthrough and
                           deliver a tour of the console. "Show me" now opens
                           the thing; the engine's landing line says what it
                           is, so there is nothing to announce first. */
                        /* 2026-09-17: the bus rides in `ops` — the shape the
                           start_takeover executor forwards and the engine's
                           composeLensFromFinding builds. Top-level busId /
                           plate were silently dropped by the executor.
                           2026-09-18: a composed topic walk reads `ops` too
                           (takeover.js composeScript), so "Show me" opens on
                           the bus Zana just raised instead of touring the
                           screen. The screen's own focus still wins. */
                        void startTakeover({
                          topic,
                          mode: 'jump',
                          skipChooser: true,
                          ...(findingTitle ? { findingTitle } : {}),
                          ...(offer?.findingKey ? { findingKey: offer.findingKey } : {}),
                          ...(busId ? { ops: { busId, plate: busId } } : {}),
                        });
                      })();
                    }}
                    style={{
                      background: 'rgba(155,124,255,.45)', border: 'none', color: '#fff',
                      borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Show me
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGuideOffer(null);
                      void fetchZanaGuide(orgCode!, { text: 'later', mode: 'reactive', pageContext });
                      setChat((c) => [...c, {
                        from: 'zana',
                        text: 'Okay — I will stay quiet. Ask me anytime.',
                      }]);
                    }}
                    style={{
                      background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#a99fce',
                      borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Later
                  </button>
                </div>
              </div>
            )}
            {chat.length === 0 ? (
              <div key={restLine} className="zana-bubble-in" style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.12)', borderRadius: '6px 16px 16px 16px', padding: '12px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.1em', color: '#c4b3ff', marginBottom: 5 }}>ZANA SAYS</div>
                <div style={{ fontSize: 13.5, lineHeight: 1.5, color: '#eee9fb', whiteSpace: 'pre-wrap' }}>{restLine}</div>
                {/* Per-screen starter prompts. She knows which tab you're on
                    (pageContext.page), so the suggestions are about THIS view —
                    one tap sends. Predicting the question is smoother than a
                    blank box that says "ask about your fleet". */}
                {suggestedPrompts.length > 0 && !asking && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {suggestedPrompts.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => send(s)}
                        style={{ background: 'rgba(155,124,255,.16)', border: '1px solid rgba(155,124,255,.35)', color: '#e6dfff', fontSize: 12, fontWeight: 600, borderRadius: 999, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {chat.map((m, i) => (
                  // maxWidth here, not on the bubble: this wrapper has the
                  // dock's width to resolve a percentage against, where the
                  // bubble only had its own text. See ZanaChatUi.
                  <div key={i} style={{ alignSelf: m.from === 'you' ? 'flex-end' : 'flex-start', maxWidth: '92%', minWidth: 0 }}>
                    <ZanaMessageBubble from={m.from} text={m.text} compact source={m.source} />
                    {m.from === 'zana' && m.restingLimit && (() => {
                      const footer = restingFooter(m.restingLimit, formatReset);
                      return (
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 6, fontSize: 10.5, color: '#c9a97a' }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f0a34a' }} />
                        <span style={{ fontWeight: 700 }}>{footer.headline}</span>
                        {footer.canBuyCredits && onRequestCredits && (
                          <button
                            type="button"
                            onClick={() => onRequestCredits?.()}
                            style={{ background: 'transparent', border: 'none', color: '#c4b3ff', fontSize: 10.5, fontWeight: 800, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                          >
                            Get more Zana credits
                          </button>
                        )}
                      </div>
                      );
                    })()}
                    {m.from === 'zana' && m.intents && m.intents.length > 0 && onExecuteIntent && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 7 }}>
                        {m.intents.map((intent, j) => {
                          const key = `${i}-${j}`;
                          const needsConfirm = intentNeedsConfirm(intent);
                          const arming = confirmKey === key;
                          const running = runningKey === key;
                          const opening = running && isNavigateIntent(intent);
                          // 2026-09-17: the checks a chip still owes — see chipMayRun.
                          const mayRun = chipMayRun(m, needsConfirm, { orgCode, takeoverActive });
                          const blockedWhy = mayRun ? undefined
                            : takeoverActive && needsConfirm ? 'Finish or stop the walkthrough first'
                              : 'Asked in another branch — ask me again here';
                          if (arming) {
                            return (
                              <span key={key} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                                <button
                                  type="button"
                                  className="zd-chip"
                                  disabled={!mayRun}
                                  title={blockedWhy}
                                  onClick={() => { if (mayRun) void runIntent(intent, key); }}
                                  style={{
                                    border: '1px solid rgba(255,180,84,.65)', background: 'rgba(255,159,28,.25)',
                                    color: '#ffe9cf', borderRadius: 999, padding: '5px 10px',
                                    fontSize: 11.5, fontWeight: 800,
                                    cursor: mayRun ? 'pointer' : 'not-allowed', opacity: mayRun ? 1 : 0.45,
                                  }}
                                >
                                  Confirm: {labelForZanaIntent(intent).toLowerCase()} ✓
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmKey(null)}
                                  style={{
                                    border: '1px solid rgba(255,255,255,.25)', background: 'transparent',
                                    color: '#c9c0e8', borderRadius: 999, padding: '5px 10px',
                                    fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                                  }}
                                >
                                  Cancel
                                </button>
                              </span>
                            );
                          }
                          return (
                            <button
                              key={key}
                              type="button"
                              className="zd-chip"
                              disabled={running || !mayRun}
                              title={blockedWhy}
                              onClick={() => { if (mayRun) onChipTap(intent, key); }}
                              style={{
                                border: opening ? '1px solid rgba(155,124,255,.9)' : needsConfirm ? '1px solid rgba(255,180,84,.5)' : '1px solid rgba(155,124,255,.45)',
                                background: needsConfirm ? 'rgba(255,159,28,.14)' : 'rgba(155,124,255,.18)',
                                color: needsConfirm ? '#ffe9cf' : '#efe9ff',
                                borderRadius: 999,
                                padding: '5px 10px',
                                fontSize: 11.5,
                                fontWeight: 700,
                                cursor: running ? 'wait' : mayRun ? 'pointer' : 'not-allowed',
                                opacity: running && !opening ? 0.6 : mayRun ? 1 : 0.45,
                                animation: opening ? 'zdGlow .8s ease-in-out infinite' : undefined,
                              }}
                            >
                              {opening
                                ? `Opening ${labelForZanaIntent(intent).replace(/^Open\s+/i, '')}…`
                                : running ? 'Working…' : `${labelForZanaIntent(intent)} ${needsConfirm ? '⚠' : '→'}`}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {/* Dual offer: stay in chat, or hand her the screen. */}
                    {m.from === 'zana' && m.offer && onExecuteIntent && (
                      <div style={{ marginTop: 9 }}>
                        <div style={{ fontSize: 12, color: '#c4b3ff', marginBottom: 6 }}>{m.offer.prompt}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                          <button
                            type="button"
                            disabled={driving}
                            onClick={() => {
                              setChat((prev) => prev.map((row, j) => (
                                j === i ? { ...row, offer: undefined } : row
                              )));
                              setChat((prev) => [...prev, {
                                from: 'zana',
                                text: 'Got it — staying in chat. Ask anything else whenever you are ready.',
                              }]);
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                              width: '100%', border: '1px solid rgba(196,179,255,.45)', borderRadius: 12,
                              padding: '10px 14px',
                              background: 'rgba(255,255,255,.06)',
                              color: '#efe9ff', fontSize: 13, fontWeight: 700,
                              cursor: driving ? 'not-allowed' : 'pointer', opacity: driving ? 0.45 : 1,
                            }}
                          >
                            {m.offer.explainLabel || 'Explain it in chat'}
                          </button>
                          <button
                            type="button"
                            disabled={driving}
                            onClick={() => void startTakeover({
                              topic: m.offer!.topic,
                              kind: m.offer!.kind === 'do' ? 'do' : 'show',
                              ...(m.offer!.kind === 'show' ? { mode: 'walk' as const } : { mode: 'walk' as const }),
                              skipChooser: m.offer!.kind === 'do',
                              ...(m.offer!.kind === 'do' ? { auto: true } : {}),
                            })}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                              width: '100%', border: 'none', borderRadius: 12, padding: '11px 14px',
                              background: m.offer.kind === 'do'
                                ? 'linear-gradient(120deg,#7a4bff,#a06bff)'
                                : 'linear-gradient(120deg,#4b3aa8,#6f4ff0)',
                              color: '#fff', fontSize: 13.5, fontWeight: 800,
                              cursor: driving ? 'not-allowed' : 'pointer', opacity: driving ? 0.45 : 1,
                              boxShadow: '0 10px 24px -12px rgba(120,80,255,.9)',
                            }}
                          >
                            <span aria-hidden>{m.offer.kind === 'do' ? '⚡' : '👉'}</span>
                            {m.offer.label}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {asking && (
                  <div style={{ alignSelf: 'flex-start' }}>
                    <ZanaThinkingBubble compact label="Reading your fleet…" />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ask input — grows up to 3 lines, then scrolls earlier text */}
          <div style={{ padding: '8px 12px 12px', borderTop: '1px solid rgba(255,255,255,.08)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea
                ref={askInputRef}
                className="zd-input zana-scroll-hide"
                value={question}
                rows={1}
                onChange={(e) => {
                  setQuestion(e.target.value);
                  const el = e.target;
                  el.style.height = 'auto';
                  // ~3 lines at 13.5px / 1.4 + vertical padding
                  const maxH = 10 + 10 + Math.ceil(13.5 * 1.4 * 3);
                  el.style.height = `${Math.min(el.scrollHeight, maxH)}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
                placeholder={
                  takeoverActive ? 'Type stop to leave the walk…'
                    : conn === 'offline' ? 'Ask locally — hello, alerts, open a tab…'
                      : 'Ask about your fleet…'
                }
                style={{
                  flex: 1, resize: 'none', overflowY: 'auto', lineHeight: 1.4,
                  background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.14)', color: '#fff',
                  borderRadius: 12, padding: '10px 13px', fontSize: 13.5, outline: 'none',
                  fontFamily: 'inherit', maxHeight: 10 + 10 + Math.ceil(13.5 * 1.4 * 3),
                }}
              />
              <button
                className="zd-send"
                onClick={() => void send()}
                disabled={asking || !question.trim()}
                style={{
                  border: 'none', cursor: asking || !question.trim() ? 'wait' : 'pointer', background: '#6f4ff0', color: '#fff', fontWeight: 800, fontSize: 13,
                  borderRadius: 12, padding: '10px 14px', opacity: asking || !question.trim() ? 0.55 : 1,
                  flexShrink: 0,
                }}
              >Ask</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
